import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar, ChevronDown } from 'lucide-react';

const presetRanges = [
  { label: 'Today', getDates: () => ({ startDate: new Date(), endDate: new Date() }) },
  {
    label: 'Last 7 Days',
    getDates: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'Last 30 Days',
    getDates: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'Last 90 Days',
    getDates: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 90);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'This Month',
    getDates: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'Last Month',
    getDates: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'This Year',
    getDates: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1);
      const end = new Date();
      return { startDate: start, endDate: end };
    },
  },
];

const DateRangePicker = ({
  dateRange,
  onChange,
  comparisonEnabled = false,
  onComparisonChange,
  dropdownAlign = 'right',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(dateRange.startDate);
  const [tempEndDate, setTempEndDate] = useState(dateRange.endDate);
  const containerRef = useRef(null);

  useEffect(() => {
    setTempStartDate(dateRange.startDate);
    setTempEndDate(dateRange.endDate);
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleApply = () => {
    onChange({ startDate: tempStartDate, endDate: tempEndDate });
    setIsOpen(false);
  };

  const handlePresetClick = (preset) => {
    const { startDate, endDate } = preset.getDates();
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    onChange({ startDate, endDate });
    setIsOpen(false);
  };

  const displayText = dateRange.startDate && dateRange.endDate
    ? `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
    : 'Select date range';

  const dropdownPositionClass = dropdownAlign === 'left' ? 'left-0' : 'right-0';

  return (
    <div ref={containerRef} className="date-range-picker relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="date-range-picker-trigger flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors shadow-sm"
      >
        <Calendar size={18} className="text-gray-500" />
        <span className="text-sm font-medium text-gray-700">{displayText}</span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`date-range-picker-panel absolute ${dropdownPositionClass} top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-[1100] p-4 min-w-[600px]`}>
          <div className="flex gap-4">
            {/* Presets */}
            <div className="w-40 border-r border-gray-100 pr-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Quick Select</h4>
              <div className="space-y-1">
                {presetRanges.map((preset) => (
                  <button
                    type="button"
                    key={preset.label}
                    onClick={() => handlePresetClick(preset)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Pickers */}
            <div className="flex-1">
              <div className="flex gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Start Date
                  </label>
                  <DatePicker
                    selected={tempStartDate}
                    onChange={(date) => setTempStartDate(date)}
                    selectsStart
                    startDate={tempStartDate}
                    endDate={tempEndDate}
                    maxDate={tempEndDate || new Date()}
                    inline
                    calendarClassName="!border-0 !shadow-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    End Date
                  </label>
                  <DatePicker
                    selected={tempEndDate}
                    onChange={(date) => setTempEndDate(date)}
                    selectsEnd
                    startDate={tempStartDate}
                    endDate={tempEndDate}
                    minDate={tempStartDate}
                    maxDate={new Date()}
                    inline
                    calendarClassName="!border-0 !shadow-none"
                  />
                </div>
              </div>

              {/* Comparison Toggle */}
              {onComparisonChange && (
                <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={comparisonEnabled}
                      onChange={(e) => onComparisonChange(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                  <span className="text-sm font-medium text-gray-700">Compare with previous period</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
